// Three.js-based renderer for the cell grid. WebGLRenderer-only: the
// scene is a single fullscreen quad sampling a DataTexture that the
// React layer rewrites every frame from the simulation snapshot, and
// 256x256 cells don't need WebGPU. The previous WebGPU dynamic import
// pointed at `three/examples/jsm/renderers/webgpu/WebGPURenderer.js`,
// a path that no longer exists in three 0.169 (the export moved to
// `three/webgpu`), so it always failed and spammed the console.

import * as THREE from "three";

import type { MaterialDef } from "../sim/types";
import { applyLightPass, buildLightTable, type LightTable } from "./lights";
import { buildStyleTable, type StyleTable } from "./styles";

export interface CellRenderer {
  /** Update the texture data from a fresh material+seed snapshot. */
  update(materialIds: Uint16Array, seeds: Uint8Array): void;
  /** Render a frame. */
  render(): void;
  /** Reattach to a new size (in CSS pixels). */
  resize(width: number, height: number): void;
  /** Reallocate the data texture for a new grid resolution. Called
   * when the viewed layer changes (e.g. entering a 128x128 dimension
   * from the 256x256 main world). No-op if the size is unchanged. */
  setGrid(width: number, height: number): void;
  /** The rendering backend in use. */
  backend(): "webgpu" | "webgl";
  /** Tear down the WebGL/WebGPU context. */
  dispose(): void;
}

export async function createRenderer(
  canvas: HTMLCanvasElement,
  gridWidth: number,
  gridHeight: number,
  palette: MaterialDef[],
): Promise<CellRenderer> {
  // `preserveDrawingBuffer: true` lets a 2D `<canvas>` overlay
  // (the magnifier) read pixels from this WebGL canvas via
  // `drawImage` *after* the next animation frame's compositor swap.
  // Without it the browser is allowed to clear the WebGL buffer
  // post-swap and the overlay reads back transparent black. Tiny
  // perf hit; fine for a 256x256 sandbox.
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: false,
    alpha: false,
    preserveDrawingBuffer: true,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setClearColor(0x000000, 1);

  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  camera.position.z = 0.5;

  // Grid resolution + texture are mutable so the viewed layer can
  // change size at runtime (main world vs. a smaller dimension).
  let gw = gridWidth;
  let gh = gridHeight;
  let data = new Uint8Array(gw * gh * 4);
  let texture = new THREE.DataTexture(data, gw, gh, THREE.RGBAFormat);
  texture.minFilter = THREE.NearestFilter;
  texture.magFilter = THREE.NearestFilter;
  texture.needsUpdate = true;

  const material = new THREE.ShaderMaterial({
    uniforms: { uTexture: { value: texture } },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = vec4(position.xy, 0.0, 1.0);
      }
    `,
    fragmentShader: `
      precision highp float;
      varying vec2 vUv;
      uniform sampler2D uTexture;
      void main() {
        vec2 uv = vec2(vUv.x, 1.0 - vUv.y);
        gl_FragColor = texture2D(uTexture, uv);
      }
    `,
    depthTest: false,
    depthWrite: false,
  });

  const geometry = new THREE.PlaneGeometry(2, 2);
  const mesh = new THREE.Mesh(geometry, material);
  scene.add(mesh);

  const styleTable: StyleTable = buildStyleTable(palette);
  const lightTable: LightTable = buildLightTable(palette);
  // Display-material set: cells whose material carries the `display`
  // tag (the screen_pixel family). The update pass draws a white
  // contour around the boundary of any contiguous display region so
  // a 16x16 screen stays visible against the dark canvas backdrop
  // even when every pixel is in its off / unlit colour.
  const isDisplay: boolean[] = palette.map((m) =>
    Array.isArray(m.tags) && m.tags.includes("display"),
  );
  let frame = 0;

  const update = (materialIds: Uint16Array, seeds: Uint8Array) => {
    if (materialIds.length * 4 !== data.length) return;
    frame++;
    const len = materialIds.length;
    for (let i = 0; i < len; i++) {
      const m = materialIds[i] >>> 0;
      const shader = styleTable[m] ?? styleTable[0];
      shader(data, i * 4, seeds[i] ?? 0, frame);
    }
    // Screen-contour pass: a cell tagged `display` whose 4-neighbour
    // is missing or non-display sits on the screen's outer edge —
    // overwrite its colour with near-white so the screen reads as a
    // bordered rectangle on top of its underlying style. Inner
    // display cells keep whatever the per-cell shader produced (the
    // off `screen_pixel`'s deep grey, the lit variants' glow, etc.).
    for (let y = 0; y < gh; y++) {
      for (let x = 0; x < gw; x++) {
        const i = y * gw + x;
        if (!isDisplay[materialIds[i] >>> 0]) continue;
        const onEdge =
          x === 0
          || y === 0
          || x === gw - 1
          || y === gh - 1
          || !isDisplay[materialIds[i - 1] >>> 0]
          || !isDisplay[materialIds[i + 1] >>> 0]
          || !isDisplay[materialIds[i - gw] >>> 0]
          || !isDisplay[materialIds[i + gw] >>> 0];
        if (!onEdge) continue;
        const o = i * 4;
        data[o] = 235;
        data[o + 1] = 240;
        data[o + 2] = 245;
        data[o + 3] = 255;
      }
    }
    // Light pass: tints surrounding cells with a soft radial glow per
    // emitter. Cheap because the kernel is small and most grids have only
    // a handful of emitters at any time.
    applyLightPass(data, materialIds, lightTable, gw, gh);
    texture.needsUpdate = true;
  };

  const resize = (width: number, height: number) => {
    renderer.setSize(width, height, false);
  };

  const setGrid = (width: number, height: number) => {
    if (width === gw && height === gh) return;
    texture.dispose();
    gw = width;
    gh = height;
    data = new Uint8Array(gw * gh * 4);
    texture = new THREE.DataTexture(data, gw, gh, THREE.RGBAFormat);
    texture.minFilter = THREE.NearestFilter;
    texture.magFilter = THREE.NearestFilter;
    texture.needsUpdate = true;
    material.uniforms.uTexture.value = texture;
  };

  const render = () => {
    renderer.render(scene, camera);
  };

  resize(canvas.clientWidth, canvas.clientHeight);

  return {
    update,
    render,
    resize,
    setGrid,
    backend: () => "webgl",
    dispose: () => {
      texture.dispose();
      geometry.dispose();
      material.dispose();
      renderer.dispose();
    },
  };
}

